import { CommonModule } from "@angular/common";
import { OVERLAY_Z_INDEX_CEILING } from "../eva-dialog/eva-z-layers";
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  forwardRef,
  inject,
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgIconsModule } from "@ng-icons/core";
import { EvaAvatarComponent } from "../eva-avatar/eva-avatar.component";
import { EvaTagComponent } from "../eva-tag/eva-tag.component";
import { isNgIconName } from "../utils/icon";

/**
 * Configuration de l'avatar pour une option
 */
export interface SelectOptionAvatar {
  /** URL de l'image */
  src?: string;
  /** Initiales à afficher si pas d'image */
  initials?: string;
  /** Couleur de fond (si initiales) */
  color?: string;
}

/**
 * Configuration du tag pour une option
 */
export interface SelectOptionTag {
  /** Texte du tag */
  label: string;
  /** Couleur sémantique (success, warning, danger, info, secondary, primary) */
  severity?:
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger"
    | "info"
    | "purple"
    | "pink"
    | "teal"
    | "olive"
    | "terracotta";
  /** Icône optionnelle (classe PrimeNG, ex: "pi pi-check") */
  icon?: string;
  /** Couleur custom (hex) — override severity quand défini (ex: "#ff6600") */
  color?: string;
  /** Variante de style du tag (défaut: soft) */
  variant?: "soft" | "filled" | "outlined";
}

export interface SelectOption {
  label: string;
  value: any;
  /**
   * Ligne secondaire grisée sous le label, pour les libellés ambigus à eux seuls
   * (ex. plusieurs catalogues du même fournisseur : le nom ne suffit pas, la gamme
   * discrimine). Entre aussi dans le champ de recherche.
   */
  description?: string;
  /** Texte non affiché, pris en compte par la recherche (ex. n° de contrat absent du libellé). */
  keywords?: string;
  icon?: string;
  disabled?: boolean;
  group?: string;
  /** Configuration de l'avatar pour cette option */
  avatar?: SelectOptionAvatar;
  /** Configuration du tag pour cette option */
  tag?: SelectOptionTag;
  /** Couleur de l'icône (token EVA ou valeur CSS, ex: "var(--eva-danger)" ou "#ff0000") */
  iconColor?: string;
  /** Couleur du texte (token EVA ou valeur CSS, ex: "var(--eva-success)" ou "#00ff00") */
  textColor?: string;
}

/**
 * EVA Select - Composant 100% custom
 *
 * Dropdown/select utilisant les tokens EVA (pas de PrimeNG).
 * Supporte recherche, groupes, icônes et multi-sélection.
 *
 * @example
 * // Select simple
 * <eva-select
 *   label="Pays"
 *   [options]="countries"
 *   [(value)]="selectedCountry"
 * ></eva-select>
 *
 * // Select avec recherche
 * <eva-select
 *   label="Client"
 *   [options]="clients"
 *   [searchable]="true"
 *   placeholder="Rechercher un client..."
 *   [(value)]="selectedClient"
 * ></eva-select>
 *
 * // Multi-select — (valueChange) émet le TABLEAU complet de la sélection,
 * // pas l'option cliquée
 * <eva-select
 *   label="Tags"
 *   [options]="tags"
 *   [multiple]="true"
 *   [(value)]="selectedTags"
 *   (valueChange)="onTagsChange($event)"
 * ></eva-select>
 *
 * // onTagsChange(values: string[]) { ... }  ← et non (value: string)
 */
@Component({
  selector: "eva-select",
  standalone: true,
  imports: [CommonModule, FormsModule, NgIconsModule, EvaAvatarComponent, EvaTagComponent],
  templateUrl: "./eva-select.component.html",
  styleUrls: ["./eva-select.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaSelectComponent),
      multi: true,
    },
  ],
})
export class EvaSelectComponent implements ControlValueAccessor, AfterViewChecked, OnDestroy {
  protected readonly isNgIconName = isNgIconName;

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Label affiché au-dessus du select */
  @Input() label: string = "";

  /** Options disponibles */
  private _options: SelectOption[] = [];
  @Input()
  set options(value: readonly unknown[] | null | undefined) {
    this._options = (value ?? []) as SelectOption[];
  }
  get options(): SelectOption[] {
    return this._options;
  }

  /** Append dropdown to body (évite les problèmes d'overflow) */
  @Input() appendToBody: boolean = true;

  /** Placeholder quand aucune sélection */
  @Input() placeholder: string = "Sélectionner...";

  /** Désactive le select */
  @Input() disabled: boolean = false;

  /** Affiche le bouton verrou/déverrouillage à gauche du trigger */
  @Input() lockable: boolean = false;

  /** État verrouillé (empêche ouverture, sélection et clear) */
  @Input() locked: boolean = false;

  /** Active le raccourci clavier Ctrl/Cmd + Shift + L */
  @Input() lockShortcutEnabled: boolean = true;

  /** Active la recherche dans les options */
  @Input() searchable: boolean = false;

  /** Placeholder du champ de recherche */
  @Input() searchPlaceholder: string = "Rechercher...";

  /** Mode multi-sélection */
  @Input() multiple: boolean = false;

  /**
   * Rend les en-têtes de groupe cliquables avec une checkbox tri-état
   * (complète / partielle / aucune) — actif uniquement en mode multi-sélection.
   */
  @Input() selectableGroups: boolean = false;

  /** Affiche un bouton clear */
  @Input() clearable: boolean = false;

  /** Ajoute un * au label */
  @Input() required: boolean = false;

  /** Message d'erreur */
  @Input() error: string = "";

  /** Texte d'aide */
  @Input() helper: string = "";

  /** Taille du select */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Layout du host : block pour les formulaires, inline pour les toolbars/filtres */
  @Input() layout: "block" | "inline" = "block";

  /** Largeur fluide du host. À désactiver pour laisser le host prendre sa largeur naturelle. */
  @Input() fluid: boolean = true;

  /** ID unique (auto-généré si non fourni) */
  @Input() inputId: string = `eva-select-${Math.random().toString(36).substr(2, 9)}`;

  /** Maximum d'items affichés dans le dropdown */
  @Input() maxDisplayedItems: number = 8;

  /** Texte quand aucune option trouvée */
  @Input() emptyMessage: string = "Aucun résultat";

  /** Largeur minimale du dropdown (ex: '320px'). Par défaut = largeur du trigger. */
  @Input() dropdownMinWidth: string | null = null;

  /** Propriété pour l'affichage du label (si options sont des objets) */
  @Input() optionLabel: string = "label";

  /** Propriété pour la valeur (si options sont des objets) */
  @Input() optionValue: string = "value";

  /** Affiche les avatars des options */
  @Input() showAvatar: boolean = false;

  /** Taille des avatars */
  @Input() avatarSize: "xsmall" | "small" | "medium" = "small";

  /** Nombre maximum d'avatars visibles en multi-select */
  @Input() maxVisibleAvatars: number = 3;

  /** Affiche les tags des options */
  @Input() showTag: boolean = false;

  /** Position du tag (left ou right, défaut: right) */
  @Input() tagPosition: "left" | "right" = "right";

  /** Affiche le label ET le tag côte à côte (au lieu de masquer le label en single-select) */
  @Input() tagWithLabel: boolean = false;

  /** Permet la création inline d'une nouvelle option */
  @Input() allowCreate: boolean = false;

  /** Mode de création : 'suggestion' (dans la liste), 'footer' (en bas), 'both' */
  @Input() createMode: "suggestion" | "footer" | "both" = "suggestion";

  /** Label du bouton de création en footer */
  @Input() createLabel: string = "Ajouter un nouveau...";

  /** Icône du bouton de création en footer */
  @Input() createIcon: string = "heroPlus";

  /** Style Notion : surligne le trigger quand un filtre est actif */
  @Input() highlightWhenActive: boolean = false;

  /** Mode combobox : texte libre + suggestions de la liste */
  @Input() editable: boolean = false;

  /**
   * Variante visuelle du select.
   * - `default` : apparence formulaire standard
   * - `filter` : apparence pill compacte (bordure arrondie, couleur brand)
   *   — idéal pour les barres de filtres
   */
  @Input() variant: "default" | "filter" = "default";

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /**
   * Émis quand la valeur change — pour le two-way binding [(value)].
   *
   * Attention : en mode `[multiple]="true"`, l'événement porte **le tableau complet de la
   * sélection**, jamais l'option qui vient d'être cliquée. Un handler typé sur une option
   * unique compile sans erreur et se trompe silencieusement à l'exécution.
   */
  @Output() valueChange = new EventEmitter<any>();

  /** Émis quand l'état verrouillé change — pour le two-way binding [(locked)] */
  @Output() lockedChange = new EventEmitter<boolean>();

  /** Émis à chaque frappe dans le champ de recherche (mode searchable) */
  @Output() onSearch = new EventEmitter<string>();

  /** Émis quand le dropdown s'ouvre */
  @Output() onOpen = new EventEmitter<void>();

  /** Émis quand le dropdown se ferme */
  @Output() onClose = new EventEmitter<void>();

  /** Émis quand Tab est pressé (pour navigation entre selects dans un tableau) */
  @Output() tabOut = new EventEmitter<void>();
  @Output() shiftTabOut = new EventEmitter<void>();

  @HostBinding("class.eva-select-host--inline")
  get isInlineLayout(): boolean {
    return this.layout === "inline";
  }

  @HostBinding("class.eva-select-host--fluid")
  get isFluid(): boolean {
    return this.fluid;
  }

  /** Émis quand l'utilisateur demande à créer une nouvelle option */
  @Output() onCreate = new EventEmitter<string>();

  // ==========================================================================
  // Internal state
  // ==========================================================================

  @ViewChild("searchInput") searchInputRef: ElementRef<HTMLInputElement>;
  @ViewChild("editableInput") editableInputRef: ElementRef<HTMLInputElement>;
  @ViewChild("optionsList") optionsListRef: ElementRef<HTMLUListElement>;
  @ViewChild("dropdownPanel") dropdownPanelRef: ElementRef<HTMLDivElement>;

  /** Focus public : cible l'input éditable ou le trigger selon le mode */
  focus(): void {
    setTimeout(() => {
      if (this.editable) {
        this.editableInputRef?.nativeElement?.focus();
      } else {
        const trigger = this.elementRef.nativeElement.querySelector(".eva-select__trigger") as HTMLElement;
        trigger?.focus();
      }
    }, 0);
  }

  _value: any = null;
  isOpen: boolean = false;
  isFocused: boolean = false;
  searchQuery: string = "";
  highlightedIndex: number = -1;

  /** Valeur affichée dans l'input éditable (mode combobox) */
  editableInputValue: string = "";
  /** Flag pour éviter le double-commit lors d'un clic sur option */
  private _optionJustSelected: boolean = false;

  // Position du dropdown (pour appendToBody)
  dropdownStyle: { [key: string]: string } = {};

  // Direction du dropdown (below ou above)
  dropdownAbove: boolean = false;

  /** Track si le dropdown a été déplacé dans le body */
  private _movedToBody: boolean = false;
  /** Listeners de scroll sur les ancêtres (pour repositionner le dropdown) */
  private _scrollListeners: (() => void)[] = [];
  /** Listeners document/window, posés seulement dropdown ouvert (cf. bindGlobalListeners) */
  private _globalListeners: (() => void)[] = [];
  private readonly zone = inject(NgZone);

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewChecked(): void {
    // Déplacer le dropdown dans le body quand il s'ouvre
    if (this.isOpen && this.appendToBody && this.dropdownPanelRef && !this._movedToBody) {
      this.renderer.appendChild(document.body, this.dropdownPanelRef.nativeElement);
      this._movedToBody = true;
      this.bindScrollListeners();
      // Re-calculer la position APRÈS le déplacement dans body, et appliquer directement sur le DOM
      this.updateDropdownPosition();
      this.applyDropdownPositionDirect();
    }
  }

  ngOnDestroy(): void {
    this.detachDropdownFromBody();
    this.unbindScrollListeners();
    this.unbindGlobalListeners();
    document.removeEventListener("keydown", this.escapeCaptureListener, true);
  }

  /**
   * Retire du `body` le panneau qu'`ngAfterViewChecked` y a déplacé.
   *
   * Ne se fie pas à `_movedToBody` : `closeDropdown()` le remet à `false` en comptant sur le
   * `*ngIf` pour retirer le nœud, ce qui ne marche que tant que le composant vit. Détruit dans
   * le même tour que la sélection -- un parent qui masque le select sur `valueChange` --, Angular
   * ne retire que l'élément hôte, et le panneau, qui n'est plus dedans, reste affiché par-dessus
   * la page sans plus aucun composant derrière lui.
   */
  private detachDropdownFromBody(): void {
    const panel = this.dropdownPanelRef?.nativeElement;
    if (panel?.parentNode === document.body) {
      document.body.removeChild(panel);
    }
    this._movedToBody = false;
  }

  onWindowChange(): void {
    if (this.isOpen && this.appendToBody) {
      this.updateDropdownPosition();
      this.cdr.markForCheck();
    }
  }

  // ==========================================================================
  // Two-way binding [(value)]
  // ==========================================================================

  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    if (val !== this._value) {
      this._value = val;
      if (this.editable) {
        this.editableInputValue = this.displayValue;
      }
      this.onChange(val);
      this.valueChange.emit(val);
    }
  }

  // ==========================================================================
  // ControlValueAccessor
  // ==========================================================================

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: any): void {
    this._value = value;
    if (this.editable) {
      this.editableInputValue = this.displayValue;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Mode combobox (editable)
  // ==========================================================================

  onEditableFocus(): void {
    this.isFocused = true;
    this.editableInputValue = this.displayValue;
    this.searchQuery = "";
    if (!this.isOpen) {
      this.openDropdown();
      this.cdr.detectChanges();
    }
  }

  onEditableInput(event: Event): void {
    if (this.disabled || this.locked) return;
    const input = event.target as HTMLInputElement;
    this.editableInputValue = input.value;
    this.searchQuery = input.value;
    this.highlightedIndex = 0;
    if (!this.isOpen) {
      this.openDropdown();
    }
    this.cdr.markForCheck();
  }

  onEditableBlur(): void {
    this.isFocused = false;
    setTimeout(() => {
      if (this._optionJustSelected) {
        this._optionJustSelected = false;
        return;
      }
      // Chercher une correspondance exacte (insensible à la casse)
      const match = this.options.find(
        (opt) => this.getOptionLabel(opt).toLowerCase() === this.editableInputValue.toLowerCase(),
      );
      if (match) {
        this._value = this.getOptionValue(match);
        this.editableInputValue = this.getOptionLabel(match);
      } else {
        // Texte libre accepté tel quel
        this._value = this.editableInputValue || null;
      }
      this.onChange(this._value);
      this.valueChange.emit(this._value);
      this.closeDropdown();
      this.cdr.markForCheck();
    }, 150);
  }

  onEditableKeydown(event: KeyboardEvent): void {
    if (this.handleLockShortcut(event)) return;
    if (this.locked) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown();
        } else {
          this.highlightNext();
        }
        this.cdr.markForCheck();
        break;
      case "ArrowUp":
        event.preventDefault();
        if (this.isOpen) {
          this.highlightPrevious();
        }
        this.cdr.markForCheck();
        break;
      case "Enter":
        event.preventDefault();
        if (this.isOpen && this.highlightedIndex >= 0) {
          const option = this.filteredOptions[this.highlightedIndex];
          if (option !== undefined && !this.isOptionDisabled(option)) {
            this.selectOption(option);
          }
        } else if (!this.isOpen) {
          this.openDropdown();
        }
        break;
      case "Escape":
        this.editableInputValue = this.displayValue;
        this.closeDropdown();
        break;
    }
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  /** Supprime les diacritiques pour la recherche (ex: "impot" → match "impôts") */
  private normalize(str: string): string {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  get filteredOptions(): SelectOption[] {
    if (!this.searchQuery) return this.options;

    const query = this.normalize(this.searchQuery);
    return this.options.filter((opt) => {
      const label = this.getOptionLabel(opt);
      if (this.normalize(label).includes(query)) return true;
      if (this.normalize(opt.description || "").includes(query)) return true;
      if (this.normalize(opt.keywords || "").includes(query)) return true;
      if (this.normalize(opt.group || "").includes(query)) return true;
      const initials = opt.avatar?.initials;
      return !!initials && this.normalize(initials).includes(query);
    });
  }

  /** Retourne le group header à afficher avant cette option, ou null si même groupe que la précédente */
  getGroupHeader(option: SelectOption, index: number): string | null {
    if (!option.group) return null;
    const filtered = this.filteredOptions;
    if (index === 0) return option.group;
    const prev = filtered[index - 1];
    return prev?.group !== option.group ? option.group : null;
  }

  /**
   * Description de l'option sélectionnée, affichée en gris à droite du label dans le
   * trigger. Mono-sélection uniquement : en multi, le trigger n'a pas la place.
   */
  get selectedDescription(): string {
    if (this.multiple || !this.hasValue) return "";
    return this.selectedOption?.description || "";
  }

  /** Titre natif du trigger : sans lui, un `label — description` tronqué reste indéchiffrable. */
  get valueTitle(): string | null {
    const description = this.selectedDescription;
    return description ? `${this.displayValue} — ${description}` : null;
  }

  optionTitle(option: SelectOption): string | null {
    return option.description ? `${this.getOptionLabel(option)} — ${option.description}` : null;
  }

  get displayValue(): string {
    if (this.multiple) {
      if (!this._value || !Array.isArray(this._value) || this._value.length === 0) {
        return "";
      }
      const selected = this.options.filter((opt) => this._value.includes(this.getOptionValue(opt)));
      if (selected.length === 1) {
        return this.getOptionLabel(selected[0]);
      }
      return `${selected.length} sélectionné(s)`;
    } else {
      const selected = this.options.find((opt) => this.getOptionValue(opt) === this._value);
      // Mode editable : si la valeur ne correspond à aucune option, c'est du texte libre
      return selected ? this.getOptionLabel(selected) : this.editable && this._value ? this._value : "";
    }
  }

  get hasValue(): boolean {
    if (this.multiple) {
      return Array.isArray(this._value) && this._value.length > 0;
    }
    if (this._value === null || this._value === undefined) return false;
    // "" est consideré comme empty SAUF si une option du dropdown expose explicitement
    // value: "" (sentinel "Aucun X" / "Tous") : on doit alors afficher son label au lieu
    // du placeholder, sinon le user voit "Sélectionner" sur une valeur pourtant matchée.
    if (this._value === "") {
      return this.options.some((opt) => this.getOptionValue(opt) === "");
    }
    return true;
  }

  get showClear(): boolean {
    return this.clearable && this.hasValue && !this.disabled && !this.locked;
  }

  // Hauteur max effective de la liste d'options — calculee a chaque ouverture dans
  // updateDropdownPosition() pour clamper au viewport disponible (evite la
  // troncature du footer de creation quand le dropdown ouvre pres du bas d'ecran).
  private clampedListMaxHeight: string | null = null;

  get dropdownMaxHeight(): string {
    if (this.clampedListMaxHeight) return this.clampedListMaxHeight;
    const itemHeight = this.size === "small" ? 32 : this.size === "large" ? 44 : 38;
    return `${this.maxDisplayedItems * itemHeight}px`;
  }

  /**
   * Options sélectionnées (pour multi-select avec avatars)
   */
  get selectedOptions(): SelectOption[] {
    if (!this.multiple || !Array.isArray(this._value)) return [];
    return this.options.filter((opt) => this._value.includes(this.getOptionValue(opt)));
  }

  /**
   * Option sélectionnée (pour single select avec avatar)
   */
  get selectedOption(): SelectOption | null {
    if (this.multiple) return null;
    return this.options.find((opt) => this.getOptionValue(opt) === this._value) || null;
  }

  /**
   * Avatars visibles en multi-select (limité à maxVisibleAvatars)
   */
  get visibleAvatars(): SelectOption[] {
    return this.selectedOptions.slice(0, this.maxVisibleAvatars);
  }

  /**
   * Nombre d'avatars cachés
   */
  get hiddenAvatarsCount(): number {
    return Math.max(0, this.selectedOptions.length - this.maxVisibleAvatars);
  }

  /**
   * Tags visibles dans le trigger en multi-select (max 2).
   * Une option sans `tag` est rendue en tag neutre portant son label, sinon le trigger reste vide.
   */
  get visibleTags(): SelectOptionTag[] {
    return this.selectedOptions
      .slice(0, 2)
      .map((opt) => opt.tag ?? { label: this.getOptionLabel(opt), severity: "secondary" });
  }

  /**
   * Nombre de tags cachés en multi-select
   */
  get hiddenTagsCount(): number {
    return Math.max(0, this.selectedOptions.length - 2);
  }

  /**
   * En single select + showTag : indique si on doit masquer le label texte
   * (l'option sélectionnée a un tag, donc on affiche uniquement le tag)
   */
  get singleTagOnly(): boolean {
    return this.showTag && !this.tagWithLabel && !this.multiple && !!this.selectedOption?.tag;
  }

  /**
   * Indique si l'option de création doit être affichée en mode suggestion
   * Conditions: allowCreate=true, query > 2 chars, aucun résultat, mode suggestion ou both
   */
  get showCreateSuggestion(): boolean {
    return (
      this.allowCreate &&
      this.searchQuery.length > 2 &&
      this.filteredOptions.length === 0 &&
      (this.createMode === "suggestion" || this.createMode === "both")
    );
  }

  /**
   * Indique si le footer de création doit être affiché
   * Conditions: allowCreate=true, mode footer ou both
   */
  get showCreateFooter(): boolean {
    return this.allowCreate && (this.createMode === "footer" || this.createMode === "both");
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  getOptionLabel(option: SelectOption): string {
    if (option === null || typeof option !== "object") return option == null ? "" : String(option);
    return (option[this.optionLabel as keyof SelectOption] as string) || "";
  }

  getOptionValue(option: SelectOption): any {
    if (option === null || typeof option !== "object") return option;
    // Utiliser `in` pour distinguer "propriété présente avec valeur undefined"
    // de "propriété absente". Ex: { label: "Tous", value: undefined } → renvoie undefined.
    if (this.optionValue in option) {
      return option[this.optionValue as keyof SelectOption];
    }
    return option;
  }

  isSelected(option: SelectOption): boolean {
    const optValue = this.getOptionValue(option);
    if (this.multiple) {
      return Array.isArray(this._value) && this._value.includes(optValue);
    }
    return this._value === optValue;
  }

  isOptionDisabled(option: SelectOption): boolean {
    return this.disabled || !!option.disabled;
  }

  // ==========================================================================
  // Sélection par groupe (tri-état)
  // ==========================================================================

  /** Indique si l'UI doit rendre les en-têtes de groupe cliquables */
  get groupSelectionEnabled(): boolean {
    return this.selectableGroups && this.multiple;
  }

  /** Retourne toutes les options non désactivées d'un groupe donné */
  private getGroupOptions(groupName: string): SelectOption[] {
    return this.options.filter((opt) => opt.group === groupName && !this.isOptionDisabled(opt));
  }

  isGroupFullySelected(groupName: string): boolean {
    const groupOpts = this.getGroupOptions(groupName);
    if (groupOpts.length === 0 || !Array.isArray(this._value)) return false;
    return groupOpts.every((opt) => this._value.includes(this.getOptionValue(opt)));
  }

  isGroupPartiallySelected(groupName: string): boolean {
    const groupOpts = this.getGroupOptions(groupName);
    if (groupOpts.length === 0 || !Array.isArray(this._value)) return false;
    const selectedCount = groupOpts.filter((opt) => this._value.includes(this.getOptionValue(opt))).length;
    return selectedCount > 0 && selectedCount < groupOpts.length;
  }

  toggleGroup(groupName: string, event?: Event): void {
    event?.stopPropagation();
    if (!this.groupSelectionEnabled || this.disabled || this.locked) return;

    const groupOpts = this.getGroupOptions(groupName);
    if (groupOpts.length === 0) return;

    const groupValues = groupOpts.map((opt) => this.getOptionValue(opt));
    const currentValues = Array.isArray(this._value) ? [...this._value] : [];
    const allSelected = this.isGroupFullySelected(groupName);

    let newValues: any[];
    if (allSelected) {
      newValues = currentValues.filter((v) => !groupValues.includes(v));
    } else {
      newValues = [...currentValues];
      for (const v of groupValues) {
        if (!newValues.includes(v)) newValues.push(v);
      }
    }

    this._value = newValues;
    this.onChange(newValues);
    this.valueChange.emit(newValues);
    this.cdr.markForCheck();
  }

  onTriggerMousedown(_event: MouseEvent): void {
    // Ne pas stopPropagation : les autres eva-select doivent recevoir document:mousedown
    // pour fermer leur dropdown via onClickOutside. Le check clickedInside protège déjà
    // le select courant (trigger = descendant de elementRef).
    this.toggleDropdown();
  }

  /** En mode éditable, le (mousedown) du trigger est désactivé pour laisser l'input
   * gérer son focus → cliquer le chevron ne faisait rien. Ce handler corrige : toggle
   * le dropdown et redonne le focus à l'input à l'ouverture. */
  onArrowMousedownEditable(event: MouseEvent): void {
    if (this.disabled || this.locked) return;
    event.preventDefault(); // évite le blur de l'input éditable
    const willOpen = !this.isOpen;
    this.toggleDropdown();
    if (willOpen) {
      setTimeout(() => this.editableInputRef?.nativeElement?.focus(), 0);
    }
  }

  toggleDropdown(): void {
    if (this.disabled || this.locked) return;

    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(): void {
    if (this.disabled || this.locked || this.isOpen) return;

    this.isOpen = true;
    this.searchQuery = "";
    this.highlightedIndex = -1;
    document.addEventListener("keydown", this.escapeCaptureListener, true);
    this.bindGlobalListeners();

    if (this.appendToBody) {
      this.updateDropdownPosition();
    }

    // Focus explicite — indispensable sur Safari/macOS où cliquer un div[tabindex=0]
    // ne lui donne pas toujours le focus, empêchant la navigation clavier.
    if (this.searchable) {
      setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 0);
    } else if (!this.editable) {
      setTimeout(() => {
        const trigger = this.elementRef.nativeElement.querySelector(".eva-select__trigger") as HTMLElement;
        trigger?.focus();
      }, 0);
    }

    this.onOpen.emit();
    this.cdr.markForCheck();
  }

  private updateDropdownPosition(): void {
    const trigger = this.elementRef.nativeElement.querySelector(".eva-select__trigger");
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const viewportMargin = 8;

    // Dimensions reelles du panneau : chrome (search + footer) + liste clampee.
    const itemHeight = this.size === "small" ? 32 : this.size === "large" ? 44 : 38;
    const searchHeight = this.searchable ? 48 : 0;
    const footerHeight = this.showCreateFooter ? 44 : 0;
    const chrome = searchHeight + footerHeight;
    const requestedListHeight = this.maxDisplayedItems * itemHeight;
    const requestedTotal = requestedListHeight + chrome;

    const spaceBelow = window.innerHeight - rect.bottom - gap - viewportMargin;
    const spaceAbove = rect.top - gap - viewportMargin;

    // Positionner au-dessus si pas assez de place en bas ET plus de place en haut
    const fitsBelow = spaceBelow >= requestedTotal;
    this.dropdownAbove = !fitsBelow && spaceAbove > spaceBelow;

    // Clamp la hauteur de la liste pour que chrome + liste tiennent dans l'espace dispo.
    const availableSpace = this.dropdownAbove ? spaceAbove : spaceBelow;
    const minListHeight = itemHeight * 3; // toujours au moins 3 options visibles
    const availableForList = Math.max(minListHeight, availableSpace - chrome);
    const finalListHeight = Math.min(requestedListHeight, availableForList);
    this.clampedListMaxHeight = `${Math.round(finalListHeight)}px`;

    const widthStyle = this.dropdownMinWidth
      ? { minWidth: this.dropdownMinWidth, width: `${rect.width}px` }
      : { width: `${rect.width}px` };

    // min-width CSS / dropdownMinWidth peuvent élargir le panneau au-delà du trigger
    const panelWidth = Math.max(rect.width, this.dropdownPanelRef?.nativeElement?.offsetWidth ?? 0);
    const left = Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - viewportMargin - panelWidth));

    if (this.dropdownAbove) {
      this.dropdownStyle = {
        position: "fixed",
        bottom: `${window.innerHeight - rect.top + gap}px`,
        left: `${left}px`,
        ...widthStyle,
      };
    } else {
      this.dropdownStyle = {
        position: "fixed",
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        ...widthStyle,
      };
    }
  }

  /** Retourne le z-index le plus élevé actuellement dans le DOM + 1 */
  private getTopZIndex(): number {
    let max = 1000;
    document.querySelectorAll("*").forEach((el) => {
      const z = parseInt(window.getComputedStyle(el).zIndex, 10);
      if (!isNaN(z) && z > max && z < OVERLAY_Z_INDEX_CEILING) max = z;
    });
    return max + 1;
  }

  /** Applique directement les styles de position sur le nœud DOM (sans attendre le binding [ngStyle]) */
  private applyDropdownPositionDirect(): void {
    const el = this.dropdownPanelRef?.nativeElement;
    if (!el || !this.dropdownStyle) return;
    Object.entries(this.dropdownStyle).forEach(([prop, value]) => {
      this.renderer.setStyle(el, prop, value as string);
    });
    this.renderer.setStyle(el, "z-index", String(this.getTopZIndex()));
  }

  // En capture : doit passer avant les écouteurs Escape `document` des dialogs / tiroirs parents
  private readonly escapeCaptureListener = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || !this.isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.editable) {
      this.editableInputValue = this.displayValue;
    }
    this.closeDropdown();
  };

  closeDropdown(): void {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.searchQuery = "";
    this.dropdownAbove = false;
    this.clampedListMaxHeight = null; // force recomputation a la prochaine ouverture
    // Retrait explicite plutôt que confié au `*ngIf` : celui-ci ne retire le nœud que si le
    // composant vit encore assez longtemps pour que sa vue soit détruite proprement.
    this.detachDropdownFromBody();
    this.unbindScrollListeners();
    this.unbindGlobalListeners();
    document.removeEventListener("keydown", this.escapeCaptureListener, true);
    this.onClose.emit();
    this.onTouched();
    this.cdr.markForCheck();
  }

  /** Écoute le scroll sur tous les ancêtres scrollables pour repositionner le dropdown */
  private bindScrollListeners(): void {
    this.unbindScrollListeners();
    let el = this.elementRef.nativeElement.parentElement;
    while (el && el !== document.body) {
      const style = getComputedStyle(el);
      if (/(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX)) {
        const listener = this.renderer.listen(el, "scroll", () => this.onWindowChange());
        this._scrollListeners.push(listener);
      }
      el = el.parentElement;
    }
  }

  /** Retire les listeners de scroll */
  private unbindScrollListeners(): void {
    this._scrollListeners.forEach((unlisten) => unlisten());
    this._scrollListeners = [];
  }

  selectOption(option: SelectOption): void {
    if (this.locked) {
      return;
    }

    if (this.isOptionDisabled(option)) return;

    const optValue = this.getOptionValue(option);

    if (!this.multiple && optValue === this._value) {
      if (this.editable) {
        this._optionJustSelected = true;
        this.editableInputValue = this.displayValue;
      }
      this.closeDropdown();
      return;
    }

    if (this.multiple) {
      const currentValues = Array.isArray(this._value) ? [...this._value] : [];
      const index = currentValues.indexOf(optValue);

      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(optValue);
      }

      this._value = currentValues;
    } else {
      this._value = optValue;
      this.closeDropdown();
    }

    if (this.editable) {
      this._optionJustSelected = true;
      this.editableInputValue = this.displayValue;
    }
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.cdr.markForCheck();
  }

  clear(event: Event): void {
    event.stopPropagation();
    if (this.locked) {
      return;
    }

    this._value = this.multiple ? [] : null;
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.cdr.markForCheck();
  }

  onSearchInput(event: Event): void {
    if (this.locked) {
      return;
    }

    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;
    this.highlightedIndex = 0;
    this.onSearch.emit(this.searchQuery);
    this.cdr.markForCheck();
  }

  handleFocus(): void {
    this.isFocused = true;
  }

  handleBlur(): void {
    this.isFocused = false;
  }

  /** Buffer pour la recherche par frappe (type-ahead) sur les selects non-searchable */
  private _typeAheadBuffer: string = "";
  private _typeAheadTimeout: any = null;

  handleKeydown(event: KeyboardEvent): void {
    if (this.handleLockShortcut(event)) {
      return;
    }

    if (this.locked) {
      return;
    }

    switch (event.key) {
      case "Enter":
        event.preventDefault();
        if (this.isOpen && this.highlightedIndex >= 0) {
          const option = this.filteredOptions[this.highlightedIndex];
          if (option !== undefined && !this.isOptionDisabled(option)) {
            this.selectOption(option);
          }
        } else {
          this.toggleDropdown();
        }
        break;

      case "Escape":
        this.closeDropdown();
        break;

      case "Tab":
        if (this.isOpen) {
          this.closeDropdown();
        }
        event.preventDefault();
        if (event.shiftKey) {
          this.shiftTabOut.emit();
        } else {
          this.tabOut.emit();
        }
        break;

      case "ArrowDown":
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown();
        } else {
          this.highlightNext();
        }
        break;

      case "ArrowUp":
        event.preventDefault();
        if (this.isOpen) {
          this.highlightPrevious();
        }
        break;

      case " ":
        if (!this.searchable) {
          event.preventDefault();
          this.toggleDropdown();
        }
        break;

      default:
        // event.key est undefined pour certains événements clavier (IME, touches spéciales,
        // événements synthétiques) selon navigateur/OS.
        if (event.key?.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          if (this.searchable && !this.isOpen) {
            // Frappe sur trigger fermé : ouvrir et injecter le 1er caractère
            this.openDropdown();
            setTimeout(() => {
              const input = this.searchInputRef?.nativeElement;
              if (input) {
                input.value = event.key;
                input.dispatchEvent(new Event("input"));
              }
            }, 0);
          } else if (!this.searchable && this.isOpen) {
            event.preventDefault();
            this.handleTypeAhead(event.key);
          }
        }
        break;
    }
  }

  private handleTypeAhead(char: string): void {
    this._typeAheadBuffer += char.toLowerCase();

    clearTimeout(this._typeAheadTimeout);
    this._typeAheadTimeout = setTimeout(() => {
      this._typeAheadBuffer = "";
    }, 500);

    const options = this.filteredOptions;
    const matchIndex = options.findIndex(
      (opt) => !this.isOptionDisabled(opt) && this.getOptionLabel(opt).toLowerCase().startsWith(this._typeAheadBuffer),
    );

    if (matchIndex >= 0) {
      this.highlightedIndex = matchIndex;
      this.cdr.detectChanges();
      this.scrollHighlightedIntoView();
    }
  }

  private highlightNext(): void {
    const options = this.filteredOptions;
    let next = this.highlightedIndex + 1;

    while (next < options.length && this.isOptionDisabled(options[next])) {
      next++;
    }

    if (next < options.length) {
      this.highlightedIndex = next;
      this.cdr.detectChanges();
      this.scrollHighlightedIntoView();
    }
  }

  private highlightPrevious(): void {
    const options = this.filteredOptions;
    let prev = this.highlightedIndex - 1;

    while (prev >= 0 && this.isOptionDisabled(options[prev])) {
      prev--;
    }

    if (prev >= 0) {
      this.highlightedIndex = prev;
      this.cdr.detectChanges();
      this.scrollHighlightedIntoView();
    }
  }

  private scrollHighlightedIntoView(): void {
    setTimeout(() => {
      const list = this.optionsListRef?.nativeElement;
      if (!list) return;
      const highlighted = list.querySelectorAll(".eva-select__option")[this.highlightedIndex] as HTMLElement;
      highlighted?.scrollIntoView({ block: "nearest" });
    }, 0);
  }

  onClickOutside(event: Event): void {
    const target = event.target as Node;
    const clickedInside = this.elementRef.nativeElement.contains(target);
    const clickedInDropdown = this._movedToBody && this.dropdownPanelRef?.nativeElement?.contains(target);
    if (!clickedInside && !clickedInDropdown) {
      this.closeDropdown();
    }
  }

  // Ouvert seulement et hors zone : un listener document permanent coûte 1 passe CD par clic.
  private bindGlobalListeners(): void {
    this.unbindGlobalListeners();
    this.zone.runOutsideAngular(() => {
      const onMousedown = (event: Event) => {
        if (this.isOpen) this.zone.run(() => this.onClickOutside(event));
      };
      const onWindowChange = () => this.zone.run(() => this.onWindowChange());
      document.addEventListener("mousedown", onMousedown);
      window.addEventListener("scroll", onWindowChange);
      window.addEventListener("resize", onWindowChange);
      this._globalListeners = [
        () => document.removeEventListener("mousedown", onMousedown),
        () => window.removeEventListener("scroll", onWindowChange),
        () => window.removeEventListener("resize", onWindowChange),
      ];
    });
  }

  private unbindGlobalListeners(): void {
    this._globalListeners.forEach((unlisten) => unlisten());
    this._globalListeners = [];
  }

  trackByFn = (_index: number, option: SelectOption): any => {
    return this.getOptionValue(option);
  };

  /**
   * Gère le click sur l'option de création (mode suggestion)
   * Émet onCreate avec la query de recherche actuelle
   */
  handleCreateClick(): void {
    if (this.locked) {
      return;
    }

    if (!this.searchQuery || this.searchQuery.length <= 2) return;

    this.onCreate.emit(this.searchQuery);
    this.closeDropdown();
  }

  /**
   * Gère le click sur le footer de création
   * Émet onCreate avec la query de recherche actuelle ou une chaîne vide
   */
  handleFooterCreateClick(): void {
    if (this.locked) {
      return;
    }

    // En mode footer, on émet même si la query est courte ou vide
    // C'est au parent de gérer ce cas (ex: ouvrir un dialog de création)
    this.onCreate.emit(this.searchQuery || "");
    this.closeDropdown();
  }

  toggleLock(event?: Event): void {
    event?.stopPropagation();

    if (!this.lockable || this.disabled) {
      return;
    }

    this.locked = !this.locked;
    if (this.locked) {
      this.closeDropdown();
    }
    this.lockedChange.emit(this.locked);
    this.cdr.markForCheck();
  }

  private handleLockShortcut(event: KeyboardEvent): boolean {
    if (!this.lockable || !this.lockShortcutEnabled || this.disabled) {
      return false;
    }

    const isLockShortcut = (event.ctrlKey || event.metaKey) && event.shiftKey && event.key?.toLowerCase() === "l";
    if (!isLockShortcut) {
      return false;
    }

    event.preventDefault();
    this.toggleLock();
    return true;
  }
}
